import { contentApi } from './content.api';

/**
 * The admin routes, injected into `contentApi` like the Agent Skill library's: the same
 * cache, the same envelope unwrap and the same bearer token. **Every route here
 * needs the `admin` scope**, which the gateway checks. The Admin tab and its guard
 * are for the person's benefit, so nobody is shown a page whose every call is a 403.
 *
 * **These types are declared here and again in the service, on purpose.** See the
 * service's ADR 0002.
 *
 * **These are the only endpoints with cache tags.** The rest of the API is read-only
 * and expires by time; an admin who adds a source needs the list to show it now.
 */

/** What an admin knows about an Agent Skill that its files do not say. */
export type GitAgentOverlay = {
  title?: string;
  summary?: string;
  /** Confluence page ids of L3 methods. */
  methods?: string[];
  /** Whether a proposed sibling file is imported. */
  include?: boolean;
};

/** The hosts a repository can be on. */
export type GitHost = 'github.com' | 'gitlab.com';

export type GitSource = {
  /** `<host>/<owner>/<repo>`, lower case. A GitLab owner can be several groups. */
  id: string;
  url: string;
  host: GitHost;
  owner: string;
  repo: string;
  credentialId?: string;
  overlay: Record<string, GitAgentOverlay>;
  marketplace?: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
  defaultBranch?: string;
  versioning?: 'tags' | 'history';
  /** Every tag imported, to the commit it named. */
  importedRefs?: Record<string, string>;
  lastCheckedAt?: string;
  lastChangedAt?: string;
  problems?: string[];
};

export type GitActor = { email: string; name?: string };

type AdminCredentialBase = {
  id: string;
  label: string;
  /** **A key is only ever sent to this host.** */
  host: GitHost;
  addedBy: GitActor;
  addedAt: string;
  rotatedBy?: GitActor;
  rotatedAt?: string;
  lastUsedAt?: string;
  lastFailureAt?: string;
  lastFailureReason?: string;
  /** The sources this key serves. */
  repos: string[];
};

/**
 * An access key's metadata. **The secret is never in a response**, nor the name of its
 * parameter. A token, from either host, expires on a date the admin entered; a GitHub App
 * has no date to show and is named by its `appId`.
 */
export type AdminCredential =
  | (AdminCredentialBase & { kind: 'github-fine-grained-pat'; expiresAt?: string })
  | (AdminCredentialBase & { kind: 'github-app'; appId: string })
  | (AdminCredentialBase & { kind: 'gitlab-token'; expiresAt?: string });

export type SyncTask = 'resync' | 'load-agents' | 'sync-prompts' | 'sync-git-sources' | 'export-docs';

export type SyncRun = {
  task: SyncTask;
  startedAt: string;
  status: 'running' | 'succeeded' | 'failed';
  /** `schedule`, or the email of the admin who pressed the button. */
  trigger: string;
  sourceId?: string;
  finishedAt?: string;
  durationMs?: number;
  summary?: Record<string, number | string | boolean>;
  problems?: string[];
};

/** A person on the Users list. `lastSignInAt` is absent until a sign-in has been recorded for them. */
export type AdminUser = {
  sub: string;
  email?: string;
  name?: string;
  lastSignInAt?: string;
  isAdmin: boolean;
};

export type UserSort = 'recent' | 'name';

export type AdminUsersPage = {
  users: AdminUser[];
  sort: UserSort;
  page: number;
  pageCount: number;
  total: number;
};

/** Where a draft field's value came from, which the admin sees beside it. */
export type Provenance = 'frontmatter' | 'metadata' | 'repo-file' | 'overlay' | 'derived' | 'missing';

export type DraftField<T> = { value?: T; from: Provenance };

export type DraftAgent = {
  name: string;
  directory: string;
  promptPath: string;
  layout: 'skill' | 'harness';
  /** A sibling `.md` the admin has to confirm. */
  sibling: boolean;
  included: boolean;
  title: DraftField<string>;
  summary: DraftField<string>;
  methods: DraftField<string[]>;
  version: DraftField<string>;
  problems: string[];
  warnings: string[];
};

export type ReleaseTag = { tag: string; version: string; name?: string };

/** The routes the toolkit tries to read a repository by, public first, then an app, then a token. GitLab has no app. */
export type AccessKind = 'public' | AdminCredential['kind'];

/** What one route said. `not-installed` is an app that is not on this repository; `unavailable` is the host not answering. */
export type AccessAttempt = {
  kind: AccessKind;
  credentialId?: string;
  label: string;
  outcome: 'reached' | 'not-installed' | 'refused' | 'cannot-see' | 'unavailable';
};

/**
 * What Check answers. **`needs-access` is a state and not an error**: a host answers
 * 404 for a private repository it will not describe, so an unreadable repository is
 * shown what was tried and offered a connection to its own host. `unavailable` is a
 * check that did not finish, for which no connection is offered.
 */
export type PreviewResult =
  | { state: 'unsupported' | 'not-found' | 'key-refused'; message: string }
  | { state: 'needs-access' | 'unavailable'; host: GitHost; message: string; tried: AccessAttempt[] }
  | {
      state: 'ready';
      source: {
        id: string;
        url: string;
        host: GitHost;
        owner: string;
        repo: string;
        defaultBranch: string;
        private: boolean;
        versioning: 'tags' | 'history';
        readAt: string;
        pluginName?: string;
        exists: boolean;
        /** How it was reached, which is what the source is saved with. */
        access: { kind: AccessKind; label: string; credentialId?: string };
      };
      releases: ReleaseTag[];
      agents: DraftAgent[];
      problems: string[];
    };

/** What GitHub said about an app the moment its key was stored. `unchecked` means the check could not be made, not that the key is wrong. */
export type CredentialCheck =
  | { state: 'verified'; name: string; slug: string; installations: { account: string; repositorySelection: 'all' | 'selected' }[] }
  | { state: 'refused'; message: string }
  | { state: 'unchecked'; message?: string };

export type CredentialAdded = { id: string; label: string; check?: CredentialCheck };

export type CredentialInput =
  | { kind: 'github-fine-grained-pat'; label: string; host: 'github.com'; secret: string; expiresAt?: string }
  | { kind: 'github-app'; label: string; host: 'github.com'; appId: string; secret: string }
  | { kind: 'gitlab-token'; label: string; host: 'gitlab.com'; secret: string; expiresAt?: string };

export type SourceInput = {
  url: string;
  credentialId?: string;
  overlay: Record<string, GitAgentOverlay>;
  marketplace?: string;
};

/** A source id is `<host>/<owner>/<repo>`: segments are encoded and the slashes kept, for the service's greedy variable. */
const encodeId = (id: string): string => id.split('/').map(encodeURIComponent).join('/');

export const adminApi = contentApi
  .enhanceEndpoints({ addTagTypes: ['Sources', 'Credentials', 'SyncRuns', 'Users'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      listAdminSources: builder.query<GitSource[], void>({
        query: () => '/content/admin/sources',
        transformResponse: (response: { sources: GitSource[] }) => response.sources,
        providesTags: ['Sources'],
      }),

      listAdminCredentials: builder.query<AdminCredential[], void>({
        query: () => '/content/admin/credentials',
        transformResponse: (response: { credentials: AdminCredential[] }) => response.credentials,
        providesTags: ['Credentials'],
      }),

      /** The newest run of each task. Absent for a task that has not run in 90 days. */
      listSyncRuns: builder.query<SyncRun[], void>({
        query: () => '/content/admin/sync-runs',
        transformResponse: (response: { runs: SyncRun[] }) => response.runs,
        providesTags: ['SyncRuns'],
      }),

      /** Every user in the pool, admins marked: one page of fifty, in the order asked for. */
      listAdminUsers: builder.query<AdminUsersPage, { sort: UserSort; page: number }>({
        query: ({ sort, page }) => `/content/admin/users?sort=${sort}&page=${String(page)}`,
        providesTags: ['Users'],
      }),

      /** Adds a person to the admin group, or removes them. Takes effect at their next token. */
      setUserAdmin: builder.mutation<{ sub: string; isAdmin: boolean }, { sub: string; admin: boolean }>({
        query: ({ sub, admin }) => ({
          url: `/content/admin/users/${encodeURIComponent(sub)}/admin`,
          method: 'PUT',
          body: { admin },
        }),
        invalidatesTags: ['Users'],
      }),

      /** Writes nothing. A fresh answer every time, so a mutation rather than a query. */
      previewSource: builder.mutation<PreviewResult, { url: string; credentialId?: string }>({
        query: (body) => ({ url: '/content/admin/sources/preview', method: 'POST', body }),
      }),

      createSource: builder.mutation<{ id: string; syncStarted: true }, SourceInput>({
        query: (body) => ({ url: '/content/admin/sources', method: 'POST', body }),
        invalidatesTags: ['Sources', 'Credentials', 'SyncRuns'],
      }),

      updateSource: builder.mutation<
        { id: string; syncStarted: true },
        { id: string; credentialId?: string | null; overlay?: Record<string, GitAgentOverlay>; marketplace?: string | null }
      >({
        query: ({ id, ...body }) => ({ url: `/content/admin/sources/${encodeId(id)}`, method: 'PUT', body }),
        invalidatesTags: ['Sources', 'Credentials', 'SyncRuns'],
      }),

      createCredential: builder.mutation<CredentialAdded, CredentialInput>({
        query: (body) => ({ url: '/content/admin/credentials', method: 'POST', body }),
        invalidatesTags: ['Credentials'],
      }),

      rotateCredential: builder.mutation<
        CredentialAdded,
        { id: string; secret: string; expiresAt?: string | null; appId?: string }
      >({
        query: ({ id, ...body }) => ({
          url: `/content/admin/credentials/${encodeURIComponent(id)}`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: ['Credentials'],
      }),

      /** The sync buttons. The run is recorded by the function itself, a moment after this answers. */
      triggerSync: builder.mutation<{ task: SyncTask; started: true }, { task: SyncTask; sourceId?: string }>({
        query: (body) => ({ url: '/content/admin/sync', method: 'POST', body }),
        invalidatesTags: ['SyncRuns'],
      }),
    }),
  });

export const {
  useListAdminSourcesQuery,
  useListAdminCredentialsQuery,
  useListSyncRunsQuery,
  useListAdminUsersQuery,
  useSetUserAdminMutation,
  usePreviewSourceMutation,
  useCreateSourceMutation,
  useUpdateSourceMutation,
  useCreateCredentialMutation,
  useRotateCredentialMutation,
  useTriggerSyncMutation,
} = adminApi;
